import { rafThrottle } from "./utils.js";

export function fixHeaderWithPadding() {
  const header = document.querySelector(".header-group");
  if (!header) return;

  const headerHeight = header.offsetHeight;

  document.body.style.paddingTop = `${headerHeight}px`;
  document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);

  header.classList.add("fixed");
  header.classList.add("is-visible");
}

export function toggleHeaderOnScroll() {
  const header = document.querySelector(".header-group");
  const searchPopup = document.querySelector(".search-popup");
  if (!header) return;

  let lastScrollY = window.scrollY;
  const removeAfter = 200;

  const onScroll = rafThrottle(() => {
    const currentScrollY = window.scrollY;
    if (searchPopup && !searchPopup.classList.contains("is-hidden")) return;

    if (currentScrollY < lastScrollY) {
      header.classList.add("is-visible");
    }

    if (currentScrollY > lastScrollY && currentScrollY > removeAfter) {
      header.classList.remove("is-visible");
    }

    lastScrollY = currentScrollY;
  });

  window.addEventListener("scroll", onScroll, { passive: true });
}

export function initHeaderMenu(options = {}) {
  const {
    menuSelector = ".js-header-sidebar-menu",
    toggleSelector = ".js-header-menu-button",
    closeSelector = ".js-header-sidebar-menu-close",
    openClass = "open",
  } = options;

  const menu = document.querySelector(menuSelector);
  if (!menu) return { destroy() {} };

  const toggles = Array.from(document.querySelectorAll(toggleSelector));
  const closeBtn = menu.querySelector(closeSelector) ?? null;

  let isOpen = false;
  const setAria = (expanded) => {
    toggles.forEach((btn) => btn.setAttribute("aria-expanded", String(Boolean(expanded))));
    menu.setAttribute("aria-hidden", String(!expanded));
  };

  const onDocumentKey = (event) => {
    if (event.key === "Escape") closeMenu();
  };

  const onDocumentDown = (event) => {
    if (!isOpen) return;
    if (!menu.contains(event.target) && toggles.every((t) => !t.contains(event.target))) {
      closeMenu();
    }
  };

  function openMenu() {
    if (isOpen) return;
    menu.classList.add(openClass);
    setAria(true);

    isOpen = true;
    document.addEventListener("keydown", onDocumentKey);
    document.addEventListener("mousedown", onDocumentDown);
  }

  function closeMenu() {
    if (!isOpen) return;
    menu.classList.remove(openClass);
    setAria(false);
    isOpen = false;
    document.removeEventListener("keydown", onDocumentKey);
    document.removeEventListener("mousedown", onDocumentDown);
  }

  function toggleMenu() {
    return isOpen ? closeMenu() : openMenu();
  }

  toggles.forEach((btn) => {
    btn.addEventListener("click", toggleMenu);
    if (!menu.id) menu.id = `header-sidebar-menu-${Math.random().toString(36).slice(2, 8)}`;
    btn.setAttribute("aria-controls", menu.id);
    btn.setAttribute("aria-expanded", "false");
  });

  if (closeBtn) closeBtn.addEventListener("click", closeMenu);

  return {
    open: openMenu,
    close: closeMenu,
    toggle: toggleMenu,
    destroy() {
      toggles.forEach((btn) => btn.removeEventListener("click", toggleMenu));
      if (closeBtn) closeBtn.removeEventListener("click", closeMenu);
      document.removeEventListener("keydown", onDocumentKey);
      document.removeEventListener("mousedown", onDocumentDown);
    },
  };
}

export function initHeaderMenuDropdown(options = {}) {
  const {
    itemSelector = ".header-menu-item--has-children",
    triggerSelector = ".js-header-menu-trigger",
    panelSelector = ".header-menu-submenu",
    openClass = "is-open",
    closeDelay = 150,
  } = options;

  const items = Array.from(document.querySelectorAll(itemSelector));
  if (!items.length) return { destroy() {} };

  const teardowns = items.map((item) => {
    const trigger = item.querySelector(triggerSelector);
    const panel = item.querySelector(panelSelector);
    if (!trigger || !panel) return () => {};

    let closeTimer = null;

    const setAria = (expanded) => {
      trigger.setAttribute("aria-expanded", String(Boolean(expanded)));
      panel.setAttribute("aria-hidden", String(!expanded));
    };

    const openDropdown = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      item.classList.add(openClass);
      setAria(true);
    };

    const closeDropdown = () => {
      item.classList.remove(openClass);
      setAria(false);
    };

    const scheduleClose = () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(closeDropdown, closeDelay);
    };

    const onMouseEnter = () => openDropdown();
    const onMouseLeave = () => scheduleClose();
    const onFocusIn = () => openDropdown();
    const onFocusOut = (event) => {
      if (item.contains(event.relatedTarget)) return;
      closeDropdown();
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closeDropdown();
      trigger.focus();
    };

    item.addEventListener("mouseenter", onMouseEnter);
    item.addEventListener("mouseleave", onMouseLeave);
    item.addEventListener("focusin", onFocusIn);
    item.addEventListener("focusout", onFocusOut);
    item.addEventListener("keydown", onKeyDown);

    setAria(false);

    return () => {
      item.classList.remove(openClass);
      item.removeEventListener("mouseenter", onMouseEnter);
      item.removeEventListener("mouseleave", onMouseLeave);
      item.removeEventListener("focusin", onFocusIn);
      item.removeEventListener("focusout", onFocusOut);
      item.removeEventListener("keydown", onKeyDown);
      if (closeTimer) window.clearTimeout(closeTimer);
    };
  });

  return {
    destroy() {
      teardowns.forEach((teardown) => teardown());
    },
  };
}
